'use client'
import React, { useState, useEffect } from 'react'
import styles from './Watchlist.module.css'
import { ConnectorManager } from '../../ConnectorManager'
import { TickerItem } from '../../types'
import { FavoritesPopup } from './FavoritesPopup'
import { useNintradeFavorites } from './hooks/useNintradeFavorites'
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import {
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'

/** Tabs at the top: Favorites, All */
const TABS = ['Favorites', 'All']

// In your Watchlist component
import { useRouter } from 'next/navigation'

export default function Watchlist() {
  // Add router
  const router = useRouter()
  const [activeTab, setActiveTab] = useState(TABS[0])
  const [tickers, setTickers] = useState<TickerItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allSymbols, setAllSymbols] = useState<string[]>([])
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TickerItem | 'symbol'
    direction: 'asc' | 'desc'
  }>({
    key: 'volume',
    direction: 'desc'
  })
  const connectorManager = ConnectorManager.getInstance()

  // Use our custom hook for favorites management
  const { favorites, addFavorite, removeFavorite, toggleFavorite } =
    useNintradeFavorites()

  useEffect(() => {
    fetchTickers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, favorites])

  // Add this function to handle symbol click
  const handleSymbolClick = (symbol: string) => {
    // Open in a new tab instead of navigating in the same tab
    // window.open(`/${symbol.toLowerCase()}`)
    // Navigate in the same tab
    window.location.href = `/${symbol.toLowerCase()}`
    // router.push(`/${symbol.toLowerCase()}`)
  }

  const fetchTickers = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Get the Binance connector
      const nintradeConnector = connectorManager.getConnector('nintrade')

      if (!nintradeConnector) {
        throw new Error('Nintrade connector not found or not connected')
      }

      // Fetch ticker data from the connector with proper typing
      const tickersData: TickerItem[] = await nintradeConnector.getTickers()

      // Extract all symbols for the search functionality
      setAllSymbols(tickersData.map(ticker => ticker.symbol))

      // Filter based on active tab
      let filteredTickers = [...tickersData]
      if (activeTab === 'Favorites') {
        filteredTickers = filteredTickers.filter(ticker =>
          favorites.includes(ticker.symbol)
        )
      }

      // Apply sorting
      sortTickers(filteredTickers)
    } catch (err: any) {
      console.error('Error fetching tickers:', err)
      setError(
        err.message ||
          'Failed to fetch market data. Please ensure you are connected to Binance.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const sortTickers = (tickersToSort: TickerItem[]) => {
    const sortedTickers = [...tickersToSort].sort((a, b) => {
      if (sortConfig.key === 'symbol') {
        return sortConfig.direction === 'asc'
          ? a.symbol.localeCompare(b.symbol)
          : b.symbol.localeCompare(a.symbol)
      }

      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : 1
      } else {
        return aValue > bValue ? -1 : 1
      }
    })

    setTickers(sortedTickers)
  }

  const handleSort = (key: keyof TickerItem | 'symbol') => {
    let direction: 'asc' | 'desc' = 'desc'

    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc'
    }

    setSortConfig({ key, direction })
    sortTickers(tickers)
  }

  const handleAddFavorite = (symbol: string) => {
    addFavorite(symbol)
    setIsPopupOpen(false)
  }

  const handleToggleFavorite = (symbol: string) => {
    toggleFavorite(symbol)
  }

  const filteredTickers = searchTerm
    ? tickers.filter(ticker =>
        ticker.symbol.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : tickers

  function renderTabContent() {
    if (isLoading)
      return (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading market data...</p>
        </div>
      )

    if (error)
      return (
        <div className={styles.errorState}>
          <p>{error}</p>
          <button onClick={fetchTickers} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )

    if (tickers.length === 0)
      return (
        <div className={styles.emptyState}>
          {activeTab === 'Favorites' ? (
            <>
              <p>No favorites added yet</p>
              <button
                onClick={() => setIsPopupOpen(true)}
                className={styles.addButton}
              >
                Add Favorites
              </button>
            </>
          ) : (
            <p>No data available</p>
          )}
        </div>
      )

    return (
      <>
        <div className={styles.tableControls}>
          <div className={styles.searchContainer}>
            <MagnifyingGlassIcon
              className={styles.searchIcon}
              width={18}
              height={18}
            />
            <input
              type="text"
              placeholder="Search symbols..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filterChips}>
            <div
              className={`${styles.filterChip} ${sortConfig.key === 'symbol' ? styles.active : ''}`}
              onClick={() => handleSort('symbol')}
            >
              Name
              {sortConfig.key === 'symbol' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'price' ? styles.active : ''}`}
              onClick={() => handleSort('price')}
            >
              Price
              <CurrencyDollarIcon className={styles.chipIcon} />
              {sortConfig.key === 'price' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'priceChangePercent' ? styles.active : ''}`}
              onClick={() => handleSort('priceChangePercent')}
            >
              Change %
              {sortConfig.key === 'priceChangePercent' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'volume' ? styles.active : ''}`}
              onClick={() => handleSort('volume')}
            >
              Volume
              <ChartBarIcon className={styles.chipIcon} />
              {sortConfig.key === 'volume' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>
          </div>

          {filteredTickers.length > 0 && (
            <div className={styles.resultsCount}>
              {filteredTickers.length} of {tickers.length}
            </div>
          )}
        </div>

        <div className={styles.tickersTable}>
          <table>
            <tbody>
              {filteredTickers.map(ticker => (
                <tr key={ticker.symbol} className={styles.tickerRow}>
                  <td
                    className={`${styles.symbolCell} ${styles.clickable}`}
                    onClick={() => handleSymbolClick(ticker.symbol)}
                  >
                    {ticker.symbol}
                  </td>
                  <td className={styles.priceCell}>
                    ${ticker.price.toFixed(ticker.price < 0.01 ? 8 : 2)}
                  </td>
                  <td
                    className={`${styles.changeCell} ${ticker.priceChangePercent >= 0 ? styles.profit : styles.loss}`}
                  >
                    {ticker.priceChangePercent >= 0 ? '+' : ''}
                    {ticker.priceChangePercent.toFixed(2)}%
                  </td>
                  <td className={styles.volumeCell}>
                    ${ticker.volume.toLocaleString()}
                  </td>
                  <td className={styles.favoriteCell}>
                    <button
                      className={styles.favoriteButton}
                      onClick={() => handleToggleFavorite(ticker.symbol)}
                      title={
                        favorites.includes(ticker.symbol)
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                    >
                      {favorites.includes(ticker.symbol) ? (
                        <StarIconSolid
                          className={styles.starIconFilled}
                          width={18}
                          height={18}
                        />
                      ) : (
                        <StarIconOutline
                          className={styles.starIcon}
                          width={18}
                          height={18}
                        />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  return (
    <div className={`${styles.watchlistContainer} ${styles.themeVariables}`}>
      <div className={styles.tabHeader}>
        {TABS.map(tab => (
          <div
            key={tab}
            className={`${styles.tab} ${tab === activeTab ? styles.active : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
        <button
          className={styles.addFavoriteButton}
          onClick={() => setIsPopupOpen(true)}
        >
          + Add
        </button>
      </div>
      <div className={styles.tabContent}>{renderTabContent()}</div>

      <FavoritesPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onAddFavorite={handleAddFavorite}
        currentFavorites={favorites}
        availableSymbols={allSymbols}
      />
    </div>
  )
}
